import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Card, CardContent } from '../../../shared/components/ui';
import { buildCategoryBreakdownData } from '../../../shared/services/insightsApi';
import { formatCurrencyCompact } from '../../../shared/utils/formatters';

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg p-3 text-sm max-w-52">
      <p className="font-semibold text-gray-900 dark:text-gray-100 mb-2 break-words">{label}</p>
      <div className="space-y-1">
        <div className="flex justify-between gap-4">
          <span className="text-gray-500 dark:text-gray-400">Avg $/SF</span>
          <span className="font-medium text-gray-900 dark:text-gray-100">${d?.avgCostPerSf?.toFixed(2)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-gray-500 dark:text-gray-400">Avg Total</span>
          <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrencyCompact(d?.avgSubtotal)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-gray-500 dark:text-gray-400">Estimates</span>
          <span className="font-medium text-gray-900 dark:text-gray-100">{d?.count}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * CategoryBreakdownChart — horizontal bar chart of average cost/SF per CSI category.
 * Props: estimates (Array)
 */
export function CategoryBreakdownChart({ estimates }) {
  const data = useMemo(() => buildCategoryBreakdownData(estimates), [estimates]);

  // Color intensity based on rank
  const getColor = (index, total) => {
    const intensity = 1 - (index / Math.max(total - 1, 1)) * 0.55;
    return `rgba(59, 130, 246, ${intensity})`;
  };

  return (
    <Card className="shadow-sm h-full">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Cost by Category</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Average cost/SF per construction division</p>
          </div>
          {data.length > 0 && (
            <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-full">
              Top {data.length}
            </span>
          )}
        </div>
      </div>

      <CardContent className="pt-4 pb-2">
        {data.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No category data available</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Estimates with cost categories will appear here
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 0, right: 48, left: 4, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis
                type="number"
                tickFormatter={(v) => `$${v}`}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                type="category"
                dataKey="category"
                width={160}
                tick={{ fontSize: 11, fill: '#374151' }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f9fafb' }} />
              <Bar dataKey="avgCostPerSf" radius={[0, 4, 4, 0]} maxBarSize={28} label={{ position: 'right', fontSize: 10, fill: '#9ca3af', formatter: (v) => `$${v}` }}>
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getColor(index, data.length)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default CategoryBreakdownChart;
